// /academy/estrategias/:id — Strategy detail page.
// Reuses the Academy shell. Includes a CTA back to the risk calculator.
import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Calculator, AlertCircle, Lightbulb, Target, Shield } from "lucide-react";
import AcademyShell from "../components/academy/AcademyShell";
import { useLanguage } from "../i18n/LanguageContext";
import { getAcademyToolsI18n } from "../i18n/academy-tools";
import { findEstrategiaLang, TIPO_COLORS, NIVEL_COLORS } from "../data/i18n-academy";

const AcademyEstrategiaDetailPage = () => {
  const { id } = useParams();
  const { lang } = useLanguage();
  const T = getAcademyToolsI18n(lang);
  const e = findEstrategiaLang(id, lang);
  if (!e) return <Navigate to="/academy/estrategias" replace />;

  return (
    <AcademyShell
      testid={`academy-estrategia-${e.id}`}
      eyebrow={`${T.estr_breadcrumb} · ${T.estr_types[e.tipo] || e.tipo}`}
      eyebrowIcon={Target}
      title={e.nombre}
      subtitle={e.descripcion}
      breadcrumbCurrent={e.nombre}
      footHint={T.estr_foot.split("·")[0].trim()}
    >
      <div className="mb-6 flex flex-wrap gap-2">
        <Badge cls={TIPO_COLORS[e.tipo]}>{T.estr_types[e.tipo] || e.tipo}</Badge>
        <Badge cls={NIVEL_COLORS[e.dificultad]}>{T.estr_levels[e.dificultad] || e.dificultad}</Badge>
        <Badge>{T.estr_markets}: {e.mercados.join(", ")}</Badge>
        <Badge>{T.estr_tf}: {e.timeframes.join(", ")}</Badge>
        <Badge>{T.estr_winrate}: {e.winRate}</Badge>
        <Badge>{T.estr_rr}: {e.ratioRR}</Badge>
      </div>

      <Section icon={Target} title={T.estr_entries} testid="estr-detail-entradas">
        <ol className="list-inside list-decimal space-y-1.5 text-[13px] leading-relaxed text-white/75">
          {e.condicionesEntrada.map((c, i) => (
            <li key={i}>{c}</li>
          ))}
        </ol>
      </Section>

      <div className="my-6 grid gap-5 lg:grid-cols-2">
        <Card title={T.estr_sl} icon={Shield} color="red">
          {e.stopLoss}
        </Card>
        <Card title={T.estr_tp} icon={Target} color="emerald">
          {e.takeProfit}
        </Card>
      </div>

      <Section title={T.estr_example} highlight>
        <p className="text-[13px] leading-relaxed text-white/80">{e.ejemploReal}</p>
      </Section>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Section icon={AlertCircle} title={T.estr_errors} color="red">
          <ul className="space-y-1.5 text-[13px] leading-relaxed text-white/75">
            {e.erroresComunes.map((m, i) => (
              <li key={i}>❌ {m}</li>
            ))}
          </ul>
        </Section>
        <Section icon={Lightbulb} title={T.estr_tips} color="orange">
          <ul className="space-y-1.5 text-[13px] leading-relaxed text-white/75">
            {e.consejosPro.map((m, i) => (
              <li key={i}>💡 {m}</li>
            ))}
          </ul>
        </Section>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <Link
          to="/academy/calculadora"
          className="inline-flex items-center gap-2 rounded-full border border-orange-400/60 bg-orange-500/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-orange-200 transition-all hover:bg-orange-500/25"
          data-testid="estr-detail-calc"
        >
          <Calculator className="h-3.5 w-3.5" />
          {T.estr_calc_cta}
        </Link>
        <Link
          to="/academy/estrategias"
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/70 hover:text-white"
        >
          {T.estr_back}
        </Link>
      </div>
    </AcademyShell>
  );
};

const Badge = ({ cls, children }) => (
  <span
    className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] ${
      cls || "border-white/10 bg-white/[0.04] text-white/65"
    }`}
  >
    {children}
  </span>
);

const Section = ({ icon: Icon, title, children, color, highlight, testid }) => (
  <div
    className={`rounded-xl border p-5 backdrop-blur-xl ${
      highlight
        ? "border-orange-400/40 bg-orange-400/[0.05]"
        : color === "red"
        ? "border-red-400/30 bg-red-400/[0.04]"
        : color === "orange"
        ? "border-orange-400/30 bg-orange-400/[0.04]"
        : "border-white/10 bg-white/[0.03]"
    }`}
    data-testid={testid}
  >
    <h3 className="mb-3 inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.22em] text-orange-300">
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {title}
    </h3>
    {children}
  </div>
);

const Card = ({ title, icon: Icon, color, children }) => (
  <div
    className={`rounded-xl border p-5 backdrop-blur-xl ${
      color === "red"
        ? "border-red-400/30 bg-red-400/[0.04]"
        : color === "emerald"
        ? "border-emerald-400/30 bg-emerald-400/[0.04]"
        : "border-white/10 bg-white/[0.03]"
    }`}
  >
    <h4 className="mb-2 inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.22em] text-white/85">
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {title}
    </h4>
    <p className="text-[13px] leading-relaxed text-white/70">{children}</p>
  </div>
);

export default AcademyEstrategiaDetailPage;
